import {
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  increment,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// TYPES
// ============================================================================

export interface UserProfile {
  userId: string;
  username: string;
  email?: string;
  photoURL?: string;  //  ADDED for avatar display

  bio?: string;       //  ADDED for profile bio

  handsWon?: number;
  handsWonWeekly?: number;
  handsWonMonthly?: number;
  followersCount?: number;
  followingCount?: number;
}

// ============================================================================
// FOLLOW / UNFOLLOW
// ============================================================================

export const followUser = async (currentUserId: string, targetUserId: string): Promise<void> => {
  if (currentUserId === targetUserId) throw new Error('Cannot follow yourself');
  try {
    const followId = currentUserId + '_' + targetUserId;
    const followRef = doc(db, 'follows', followId);
    await setDoc(followRef, {
      followerId: currentUserId,
      followingId: targetUserId,
      createdAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'users', currentUserId), { followingCount: increment(1) });
    await updateDoc(doc(db, 'users', targetUserId), { followersCount: increment(1) });
  } catch (error) {
    console.error('Error following user:', error);
    throw error;
  }
};

export const unfollowUser = async (currentUserId: string, targetUserId: string): Promise<void> => {
  try {
    const followId = currentUserId + '_' + targetUserId;
    await deleteDoc(doc(db, 'follows', followId));
    await updateDoc(doc(db, 'users', currentUserId), { followingCount: increment(-1) });
    await updateDoc(doc(db, 'users', targetUserId), { followersCount: increment(-1) });
  } catch (error) {
    console.error('Error unfollowing user:', error);
    throw error;
  }
};

export const isFollowing = async (currentUserId: string, targetUserId: string): Promise<boolean> => {
  try {
    const followId = currentUserId + '_' + targetUserId;
    const snapshot = await getDoc(doc(db, 'follows', followId));
    return snapshot.exists();
  } catch (error) {
    return false;
  }
};

// ============================================================================
// GET FOLLOWERS / FOLLOWING
// ============================================================================

export const getFollowers = async (uid: string): Promise<UserProfile[]> => {
  try {
    const q = query(collection(db, 'follows'), where('followingId', '==', uid));
    const snapshot = await getDocs(q);
    const followers: UserProfile[] = [];
    for (const docSnap of snapshot.docs) {
      const userSnap = await getDoc(doc(db, 'users', docSnap.data().followerId));
      if (userSnap.exists()) {
        followers.push({ userId: userSnap.id, ...userSnap.data() } as UserProfile);
      }
    }
    return followers;
  } catch (error) {
    console.error('Error getting followers:', error);
    return [];
  }
};

export const getFollowing = async (uid: string): Promise<UserProfile[]> => {
  try {
    const q = query(collection(db, 'follows'), where('followerId', '==', uid));
    const snapshot = await getDocs(q);
    const following: UserProfile[] = [];
    for (const docSnap of snapshot.docs) {
      const userSnap = await getDoc(doc(db, 'users', docSnap.data().followingId));
      if (userSnap.exists()) {
        following.push({ userId: userSnap.id, ...userSnap.data() } as UserProfile);
      }
    }
    return following;
  } catch (error) {
    console.error('Error getting following:', error);
    return [];
  }
};

// ============================================================================
// FRIENDS (MUTUAL FOLLOWS)
// ============================================================================

/**
 * Get mutual follows (friends) - users who follow each other
 * A friend is someone you follow AND who follows you back
 */
export const getFriends = async (uid: string): Promise<UserProfile[]> => {
  try {
    // Get users I'm following
    const followingQuery = query(collection(db, 'follows'), where('followerId', '==', uid));
    const followingSnapshot = await getDocs(followingQuery);
    const followingIds = new Set<string>();
    followingSnapshot.forEach(docSnap => {
      followingIds.add(docSnap.data().followingId);
    });

    // Get users following me
    const followersQuery = query(collection(db, 'follows'), where('followingId', '==', uid));
    const followersSnapshot = await getDocs(followersQuery);
    const followerIds = new Set<string>();
    followersSnapshot.forEach(docSnap => {
      followerIds.add(docSnap.data().followerId);
    });

    // Find mutual follows (intersection)
    const mutualIds: string[] = [];
    followingIds.forEach(id => {
      if (followerIds.has(id)) {
        mutualIds.push(id);
      }
    });

    // Fetch user profiles for mutual follows
    const friends: UserProfile[] = [];
    for (const friendId of mutualIds) {
      const userSnap = await getDoc(doc(db, 'users', friendId));
      if (userSnap.exists()) {
        friends.push({ userId: userSnap.id, ...userSnap.data() } as UserProfile);
      }
    }

    return friends;
  } catch (error) {
    console.error('Error getting friends:', error);
    return [];
  }
};

/**
 * Check if two users are mutual friends
 */
export const isFriend = async (userId1: string, userId2: string): Promise<boolean> => {
  try {
    // Check if userId1 follows userId2
    const followId1 = userId1 + '_' + userId2;
    const follow1Snap = await getDoc(doc(db, 'follows', followId1));
    
    if (!follow1Snap.exists()) return false;
    
    // Check if userId2 follows userId1
    const followId2 = userId2 + '_' + userId1;
    const follow2Snap = await getDoc(doc(db, 'follows', followId2));
    
    return follow2Snap.exists();
  } catch (error) {
    console.error('Error checking friendship:', error);
    return false;
  }
};

/**
 * Get friend count (mutual follows)
 */
export const getFriendsCount = async (uid: string): Promise<number> => {
  try {
    const friends = await getFriends(uid);
    return friends.length;
  } catch (error) {
    console.error('Error getting friends count:', error);
    return 0;
  }
};

// ============================================================================
// USER SEARCH - Enhanced with currentUserId exclusion and fallback
// ============================================================================

/**
 * Search for users by username
 * @param searchQuery - The search term (min 2 characters)
 * @param currentUserId - Current user ID to exclude from results (optional)
 * @param maxResults - Maximum number of results to return
 */
export const searchUsers = async (
  searchQuery: string, 
  currentUserId?: string,
  maxResults: number = 20
): Promise<UserProfile[]> => {
  try {
    if (!searchQuery || searchQuery.length < 2) return [];
    
    const searchLower = searchQuery.toLowerCase();
    
    // Primary search: Try Firestore range query
    const q = query(
      collection(db, 'users'),
      where('username', '>=', searchLower),
      where('username', '<=', searchLower + '\uf8ff'),
      limit(maxResults + 1) // Get extra to account for filtering current user
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.size > 0) {
      const results: UserProfile[] = [];
      snapshot.docs.forEach(d => {
        // Skip current user if provided
        if (currentUserId && d.id === currentUserId) return;
        results.push({ userId: d.id, ...d.data() } as UserProfile);
      });
      return results.slice(0, maxResults);
    }
    
    // Fallback: Client-side filtering for case-insensitive search
    // This handles cases where usernames aren't stored lowercase
    const allUsersQuery = query(collection(db, 'users'), limit(200));
    const allSnapshot = await getDocs(allUsersQuery);
    
    const results: UserProfile[] = [];
    allSnapshot.docs.forEach(d => {
      // Skip current user if provided
      if (currentUserId && d.id === currentUserId) return;
      
      const data = d.data();
      const username = (data.username || '').toLowerCase();
      
      if (username.includes(searchLower)) {
        results.push({ userId: d.id, ...data } as UserProfile);
      }
    });
    
    return results.slice(0, maxResults);
  } catch (error) {
    console.error('Error searching users:', error);
    return [];
  }
};

// ============================================================================
// SUGGESTED USERS - NEW: Get active players to follow
// ============================================================================

/**
 * Get suggested users to follow based on:
 * 1. Users with most hands won (active players)
 * 2. Users not already followed by current user
 * 3. Excludes current user
 * 
 * @param currentUserId - Current user ID to exclude
 * @param maxResults - Maximum number of suggestions to return
 */
export const getSuggestedUsers = async (
  currentUserId: string,
  maxResults: number = 10
): Promise<UserProfile[]> => {
  try {
    // Get users the current user is already following
    const followingQuery = query(
      collection(db, 'follows'), 
      where('followerId', '==', currentUserId)
    );
    const followingSnapshot = await getDocs(followingQuery);
    const followingIds = new Set<string>();
    followingSnapshot.forEach(docSnap => {
      followingIds.add(docSnap.data().followingId);
    });
    
    // Get users - try sorting by stats.handsWon first
    let suggestions: UserProfile[] = [];
    
    try {
      // Try to get users sorted by hands won (most active players)
      const activeUsersQuery = query(
        collection(db, 'users'),
        orderBy('stats.handsWon', 'desc'),
        limit(50)
      );
      const activeSnapshot = await getDocs(activeUsersQuery);
      
      activeSnapshot.forEach(d => {
        // Skip current user and already-followed users
        if (d.id === currentUserId || followingIds.has(d.id)) return;
        suggestions.push({ userId: d.id, ...d.data() } as UserProfile);
      });
    } catch (orderError) {
      // stats.handsWon ordering might not be indexed, fall through
      console.log('Stats ordering not available, using fallback');
    }
    
    // If we don't have enough suggestions, get more users without ordering
    if (suggestions.length < maxResults) {
      const moreUsersQuery = query(
        collection(db, 'users'),
        limit(100)
      );
      const moreSnapshot = await getDocs(moreUsersQuery);
      
      const existingIds = new Set(suggestions.map(s => s.userId));
      
      moreSnapshot.forEach(d => {
        // Skip current user, already-followed, and already in suggestions
        if (d.id === currentUserId || followingIds.has(d.id) || existingIds.has(d.id)) return;
        suggestions.push({ userId: d.id, ...d.data() } as UserProfile);
      });
    }
    
    // Sort by handsWon (most active first) if available
    suggestions.sort((a, b) => {
      const aWins = a.handsWon || (a as any).stats?.handsWon || 0;
      const bWins = b.handsWon || (b as any).stats?.handsWon || 0;
      return bWins - aWins;
    });
    
    return suggestions.slice(0, maxResults);
      
  } catch (error) {
    console.error('Error getting suggested users:', error);
    return [];
  }
};

// ============================================================================
// GET ALL USERS
// ============================================================================

export const getAllUsers = async (maxResults: number = 100): Promise<UserProfile[]> => {
  try {
    const q = query(collection(db, 'users'), orderBy('username'), limit(maxResults));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ userId: d.id, ...d.data() } as UserProfile));
  } catch (error) {
    console.error('Error getting all users:', error);
    return [];
  }
};

// ============================================================================
// GET USER PROFILE
// ============================================================================

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const snapshot = await getDoc(doc(db, 'users', uid));
    if (snapshot.exists()) {
      return { userId: snapshot.id, ...snapshot.data() } as UserProfile;
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Get user's email (for notifications)
 */
export const getUserEmail = async (uid: string): Promise<string | null> => {
  try {
    const snapshot = await getDoc(doc(db, 'users', uid));
    if (snapshot.exists()) {
      return snapshot.data().email || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting user email:', error);
    return null;
  }
};

// ============================================================================
// FRIENDS AT TABLES - For "Join Friends Table" feature
// ============================================================================

export interface FriendAtTable {
  odid: string;
  username: string;
  photoURL?: string;
  tableId: string;
  tableName: string;
  currency: 'playcoins' | 'pkcash';
  smallBlind: number;
  bigBlind: number;
  playerCount: number;
  maxSeats: number;
}

/**
 * Get mutual friends who are currently at public tables
 */
export const getFriendsAtTables = async (userId: string): Promise<FriendAtTable[]> => {
  try {
    const friends = await getFriends(userId);
    if (friends.length === 0) return [];

    const friendIds = friends.map(f => f.userId);
    const tablesRef = collection(db, 'tables');
    const activeTablesQuery = query(tablesRef, where('status', 'in', ['waiting', 'playing', 'ready']));
    const tablesSnapshot = await getDocs(activeTablesQuery);

    const friendsAtTables: FriendAtTable[] = [];

    for (const tableDoc of tablesSnapshot.docs) {
      const tableData = tableDoc.data();
      if (tableData.isPrivate) continue;

      const tablePlayers: string[] = tableData.players || [];
      
      for (const friendId of friendIds) {
        if (tablePlayers.includes(friendId)) {
          const friend = friends.find(f => f.userId === friendId);
          if (friend) {
            friendsAtTables.push({
              odid: friend.userId,
              username: friend.username || 'Unknown',
              photoURL: (friend as any).photoURL,
              tableId: tableDoc.id,
              tableName: tableData.name || 'Unnamed Table',
              currency: tableData.currency || 'playcoins',
              smallBlind: tableData.smallBlind || 1,
              bigBlind: tableData.bigBlind || 2,
              playerCount: tablePlayers.length,
              maxSeats: tableData.maxSeats || 6,
            });
          }
        }
      }
    }
    return friendsAtTables;
  } catch (error) {
    console.error('Error getting friends at tables:', error);
    return [];
  }
};

/**
 * Subscribe to friends at tables (real-time)
 */
export const subscribeToFriendsAtTables = (
  userId: string,
  onUpdate: (friends: FriendAtTable[]) => void
): (() => void) => {
  let friendIds: string[] = [];
  let friendsMap: Map<string, UserProfile> = new Map();
  let latestSnapshot: any = null;
  let friendsLoaded = false;

  // Helper to process tables with current friends data
  const processSnapshot = () => {
    if (!friendsLoaded || !latestSnapshot) return;
    
    if (friendIds.length === 0) {
      onUpdate([]);
      return;
    }

    const friendsAtTables: FriendAtTable[] = [];

    for (const tableDoc of latestSnapshot.docs) {
      const tableData = tableDoc.data();
      if (tableData.isPrivate) continue;

      const tablePlayers: string[] = tableData.players || [];
      
      for (const friendId of friendIds) {
        if (tablePlayers.includes(friendId)) {
          const friend = friendsMap.get(friendId);
          if (friend) {
            friendsAtTables.push({
              odid: friend.userId,
              username: friend.username || 'Unknown',
              photoURL: (friend as any).photoURL,
              tableId: tableDoc.id,
              tableName: tableData.name || 'Unnamed Table',
              currency: tableData.currency || 'playcoins',
              smallBlind: tableData.smallBlind || 1,
              bigBlind: tableData.bigBlind || 2,
              playerCount: tablePlayers.length,
              maxSeats: tableData.maxSeats || 6,
            });
          }
        }
      }
    }
    console.log(`👥 [FriendsAtTables] Found ${friendsAtTables.length} friends at tables`);
    onUpdate(friendsAtTables);
  };

  // Load friends first
  getFriends(userId).then(friends => {
    friendIds = friends.map(f => f.userId);
    friends.forEach(f => friendsMap.set(f.userId, f));
    friendsLoaded = true;
    console.log(`👥 [FriendsAtTables] Loaded ${friends.length} friends`);
    processSnapshot(); // Process any snapshot we already received
  }).catch(err => {
    console.error('Error loading friends:', err);
    friendsLoaded = true;
    onUpdate([]);
  });

  // Subscribe to tables
  const tablesRef = collection(db, 'tables');
  const activeTablesQuery = query(tablesRef, where('status', 'in', ['waiting', 'playing', 'ready']));

  const unsubscribe = onSnapshot(activeTablesQuery, (snapshot) => {
    latestSnapshot = snapshot;
    processSnapshot(); // Will only process if friends are loaded
  }, (error) => {
    console.error('Error subscribing to tables:', error);
    onUpdate([]);
  });

  return unsubscribe;
};